import { useState } from 'react';
import { Form, Button, Alert } from 'react-bootstrap';
import { Navigate, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import api from '../lib/api';
import { useAuth } from '../lib/auth.jsx';

export default function LoginPage() {
  const { login, verifyMfa, user, loading } = useAuth();
  const navigate = useNavigate();
  const [stage, setStage] = useState('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [recovery, setRecovery] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [challengeToken, setChallengeToken] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  // If we land on /login with an already-valid session (e.g. session restored
  // by bootstrap), bounce to the dashboard via the router (no useEffect, no race).
  if (!loading && user) {
    return <Navigate to="/" replace />;
  }

  const formatError = (err) => {
    if (err.response?.data?.message) return err.response.data.message;
    if (err.response?.data?.code) return `${err.response.data.code} (HTTP ${err.response.status})`;
    if (err.response?.status) return `HTTP ${err.response.status}`;
    if (err.message) return `${err.message}`;
    return 'Login fehlgeschlagen';
  };

  const submitCredentials = async (e) => {
    e.preventDefault();
    setError(null); setBusy(true);
    try {
      const r = await login({ email, password });
      if (r.stage === 'mfa') {
        setChallengeToken(r.mfaChallengeToken);
        setStage('mfa');
      } else {
        navigate(r.mustChangePassword ? '/profile' : '/');
      }
    } catch (err) {
      console.error('Login error', err);
      setError(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setError(null); setBusy(true);
    try {
      const r = await verifyMfa({
        challengeToken,
        totpToken: useRecovery ? undefined : totp,
        recoveryCode: useRecovery ? recovery : undefined
      });
      navigate(r.mustChangePassword ? '/profile' : '/');
    } catch (err) {
      console.error('MFA error', err);
      setError(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <div className="text-center mb-4">
          <div className="d-inline-flex align-items-center gap-2 mb-2">
            <span style={{ color: 'var(--bs-primary)' }}>●</span>
            <span style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 700, fontSize: '1.4rem' }}>
              UAV-BOS Kontor
            </span>
          </div>
          <p className="text-muted small mb-0">
            {stage === 'credentials' ? 'Anmelden' : 'Zwei-Faktor-Authentifizierung'}
          </p>
        </div>

        {error && <Alert variant="danger" className="py-2">{error}</Alert>}

        {stage === 'credentials' && (
          <Form onSubmit={submitCredentials}>
            <Form.Group className="mb-3">
              <Form.Label>E-Mail</Form.Label>
              <Form.Control type="email" autoComplete="username" required
                value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
            </Form.Group>
            <Form.Group className="mb-4">
              <Form.Label>Passwort</Form.Label>
              <Form.Control type="password" autoComplete="current-password" required
                value={password} onChange={(e) => setPassword(e.target.value)} />
            </Form.Group>
            <Button type="submit" className="w-100" disabled={busy}>
              {busy ? 'Anmelden…' : 'Anmelden'}
            </Button>
          </Form>
        )}

        {stage === 'mfa' && (
          <Form onSubmit={submitMfa}>
            {!useRecovery && (
              <Form.Group className="mb-3">
                <Form.Label>Code aus deiner Authenticator-App</Form.Label>
                <Form.Control type="text" inputMode="numeric" maxLength={6} required
                  value={totp} onChange={(e) => setTotp(e.target.value.replace(/\D/g, ''))} autoFocus />
              </Form.Group>
            )}
            {useRecovery && (
              <Form.Group className="mb-3">
                <Form.Label>Recovery-Code</Form.Label>
                <Form.Control type="text" required
                  value={recovery} onChange={(e) => setRecovery(e.target.value.toUpperCase())} autoFocus
                  placeholder="ABCDE-12345" />
              </Form.Group>
            )}
            <Button type="submit" className="w-100 mb-2" disabled={busy}>
              {busy ? 'Prüfen…' : 'Bestätigen'}
            </Button>
            <Button type="button" variant="link" size="sm" className="w-100"
              onClick={() => { setUseRecovery(!useRecovery); setError(null); }}>
              <Icon icon="tabler:key" className="me-1" />
              {useRecovery ? 'Authenticator-Code stattdessen' : 'Recovery-Code verwenden'}
            </Button>
          </Form>
        )}
      </div>
    </div>
  );
}
